/**
 * Responsibility: the four figures across the top of the grading page.
 *
 * Split out to keep the page file to the job of wiring. The figures
 * themselves are chosen to answer "is there anything here" before the
 * table is read: how many cards clear the costs, the best one, how many
 * are already marked for a submission, and how many were looked at and
 * turned down.
 *
 * "Worth a look" is qualified by how many of those rest on completed
 * sales rather than on what a seller is asking, because the two are not
 * the same claim and the gap on graded singles runs to a third.
 */
import Box from '@mui/material/Box';
import { MetricCard } from '@kit/components';
import type { Opportunity } from '../../api/client';
import { money } from './money';

type GradingStatsProps = {
  opportunities: Opportunity[];
  /** Those priced off completed sales and not falling sharply. */
  sound: number;
  /** Marked for a submission. */
  flagged: number;
  rejected: number;
};

export function GradingStats({
  opportunities,
  sound,
  flagged,
  rejected,
}: GradingStatsProps) {
  const best = opportunities[0];

  return (
    <Box
      sx={{
        display: 'grid',
        gap: 2,
        gridTemplateColumns: { xs: '1fr', sm: 'repeat(2, 1fr)', lg: 'repeat(4, 1fr)' },
      }}
    >
      <MetricCard
        label="Worth a look"
        value={String(opportunities.length)}
        helper={`${sound} on sold prices and holding`}
      />
      <MetricCard
        label="Best payoff"
        value={best ? money(best.single_gem.profit) : '—'}
        helper={best ? best.card_name : 'nothing clears costs yet'}
      />
      <MetricCard label="Flagged" value={String(flagged)} helper="marked for a submission" />
      <MetricCard
        label="Passed over"
        value={String(rejected)}
        helper="with a reason each"
      />
    </Box>
  );
}
